import { Map } from 'immutable';
import get from 'lodash/get';
import * as authActionTypes from '../auth/AuthActionTypes';
import * as resourcesActionTypes from './ResourcesActionTypes';
import { bulkActions } from '../../data/Constants';
import * as ChartUtils from '../../utils/ChartUtils';
import * as ChartConstants from '../../components/Home/Home/ChartConstants';
import cloneDeep from 'lodash/cloneDeep';

// action.resourcePath --> resource, resourceId, subResource, subResourceId,...
export default function ResourcesReducer(state = Map(), action) {
  if (action.type === authActionTypes.SIGNOUT_USER) return Map();
  if (action.type === resourcesActionTypes.GET_DOWNLOAD_RESOURCES_SUCCESS) return state;
  if (action.type === resourcesActionTypes.UPLOAD_RESOURCE_SUCCESS) return state;
  if (action.type === resourcesActionTypes.UPDATE_PAGE_SETTINGS) {
    // action.keyPath = ['pageSettings', 'pageName', 'pageUiStateKey', 'pageUiStateSubKey', ...]
    const { keyPath, value } = action;
    return state.setIn(keyPath, value);
  }
  if (action.type === resourcesActionTypes.FILTER_PRODUCTS) {
    if (action.category === null) {
      return state.set('filteredProducts', state.get('products'));
    }
    return state.set('filteredProducts', state.get('products').filter(item => action.category.includes(item.category.name)));
  }
  if (action.type === resourcesActionTypes.GET_RECENT_ORDERS_SUCCESS) {
    return state.set('recentOrders', action.recentOrders.data);
  }
  if (!isFromBatchRequest(action) && !action.resourcePath) return state;

  if (isGetOrdersAnalyticsSuccess(action)) return state.set(ChartConstants.ORDERS_ANALYTICS_API_ROUTE, action.response);
  if (isGetAppDownloadsSuccess(action)) return state.set(ChartConstants.DOWNLOADS_ANALYTICS_API_ROUTE, action.response);

  const resourcePath = isFromBatchRequest(action)
    ? getSampleBatchRequestResourcePath(action)
    : action.resourcePath;

  return createNewParentState(state, resourcePath, action);
}

/**
 * Recursive function.
 * For e.g. /products/:id/items/:item_id/out-of-stock, the first parentState is not `products`,
 * but rather the entire `resources` state in Redux. The `products` list is a child resource list
 * in the first iteration.
 *
 * Each call eventually updates a parent state object's key having a resource list value
 * with a new resource list containing updated child resource elements having particular ids from
 * the path, and returns new objects/lists for immutability.
 *
 * It will crawl one level deeper per call.
 *
 * A `path` like /products/:id/items/:item_id/out-of-stock has 3 levels, and will run the `hasSubResource` block,
 * aiming to update the `resources.products` key with a products list having an updated product with id :id.
 *
 * It will strip the `path` to /items/:item_id/out-of-stock (2 levels) for the next call. This next call also
 * runs the `hasSubResource` block, aims to update the parent state, a single product, with an updated child
 * resource list, an items list. That items list will contain a new item with id :item_id.
 * Then it strips the `path` to /out-of-stock for the next call.
 *
 * This final call would run `updateDeepestChild`, updating the item's out-of-stock value,
 * returning the item, and starting the process returning values to the other calls.
 *
 * Starting paths like /products/:id/items/:item_id and /products/:id/items would have 2 levels.
 * Starting paths like /products/:id and /products have 1 level, running updateDeepestChild right away,
 * updating `resources.products`.
 *
 * Merging batched responses starts similarly until we find the second deepest level. If we are fetching many
 * /product/:id/items/:item_id/out-of-stock responses, when the `path` becomes /items/:item_id/out-of-stock,
 * it updates each item with its out-of-stock response value, updates the item list with all the new items,
 * and sets the item list on the one product.
 */
const createNewParentState = (parentState, path, action) => {
  let {
    resourceId,
    resource,
    subResource,
    queryParam,
  } = getPathParts(path, action);

  if (isOrderDetailsFetch(resource, subResource)) return handleOrderDetailsFetch(parentState, action);

  if (hasSubResource(path)) {
    if (isFromBatchRequest(action) && isSecondDeepestResource(path)) {
      const batchMergeArgs = {
        action,
        parentState,
        resource,
        path,
        queryParam,
      };
      return mergeResponsesOnSecondDeepestResource(batchMergeArgs);
    }

    const matchingIdChildResource = parentState.get(resource).filter(resource => resource.id == resourceId)[0];
    const nextPath = path.slice(2);
    const updatedSingleChildResource = createNewParentState(Map(matchingIdChildResource), nextPath, action);

    const updateChildResourceListArgs = {
      parentState,
      updatedSingleChildResource,
      resourceId,
      resource,
      subResource,
      action,
      path,
    };

    return updateChildResourceListOnParent(updateChildResourceListArgs);
  }

  const updateArgs = {
    parentState,
    action,
    resource,
    resourceId,
    queryParam,
  };
  return updateDeepestChild(updateArgs);
};

const updateList = (resourceList, idToUpdatedResourceMap) => {
  return resourceList.map((resource) => {
    const { id } = resource;
    const updatedResource = idToUpdatedResourceMap[id];
    if (!updatedResource) return resource;
    return Map(resource).merge(updatedResource).toJS();
  });
};

const isFromBatchRequest = action => !!action.responses;
const isSecondDeepestResource = path => path.length > 2 && path.length < 5;

/**
 * For e.g. /products/:id/items/:item_id/out-of-stock, this would take a bunch of out-of-stock responses for each item, and
 * 1. Merge them on their respective items
 * 2. Update the items list with just the newly changed items
 * 3. Update the items key on a product with the new items list
 */
const mergeResponsesOnSecondDeepestResource = (args) => {
  const { parentState, resource } = args;
  const secondDeepestResourceList = parentState.get(resource);
  const idToUpdatedResourceMap = getIdToUpdatedResourceMap(args);
  const updatedSecondDeepestResourceList = updateList(secondDeepestResourceList, idToUpdatedResourceMap);
  return parentState.set(resource, updatedSecondDeepestResourceList);
};

const hasSubResource = path => path.length > 2;

const getIdToUpdatedResourceMap = ({
  action,
  parentState,
  resource,
  path: recursiveIterationLeftoverPath,
  queryParam,
}) => {
  const idToUpdatedResourceMap = {};
  const { responses } = action;

  const secondDeepestResourceList = parentState.get(resource);
  responses.forEach((response) => {
    if (!response || response.status !== 'fulfilled' || !response.value) return;

    const { data, resourcePath: currentResponseResourcePath } = response.value;
    const originalPathLength = currentResponseResourcePath.length;
    const leftoverPathLength = recursiveIterationLeftoverPath.length;
    const difference = originalPathLength - leftoverPathLength;

    const pathFromSecondDeepestToDeepest = currentResponseResourcePath.slice(difference);
    const lastPathSegment = recursiveIterationLeftoverPath[leftoverPathLength - 1];
    const secondDeepestResourceId = pathFromSecondDeepestToDeepest[1];

    const secondDeepestResource = Map(secondDeepestResourceList.filter(resource => resource.id == secondDeepestResourceId)[0]);

    const actionLike = {
      response: data,
      resourcePath: pathFromSecondDeepestToDeepest,
      type: resourcesActionTypes.BATCH_UPDATE_AFTER_GET_ALL_RESOURCES,
    };
    const updateArgs = {
      parentState: secondDeepestResource,
      action: actionLike,
      resource: lastPathSegment,
      resourceId: secondDeepestResourceId,
      queryParam,
    };
    idToUpdatedResourceMap[secondDeepestResourceId] = updateDeepestChild(updateArgs);
  });
  return idToUpdatedResourceMap;
};

export function getSampleBatchRequestResourcePath(action) {
  const { responses } = action;
  const fulfilledRequest = responses.find(response => response.status === 'fulfilled');
  return get(fulfilledRequest, 'value.resourcePath');
}

/**
 * For example, for products/:id/items/:item_id/out-of-stock, this would update the
 * out-of-stock key on an item
 */
const updateDeepestChild = ({
  parentState,
  action,
  resource,
  resourceId,
  queryParam,
}) => {
  let updatedState = update(parentState, action, resource, resourceId, queryParam);
  if (resource === 'products') updatedState = handleProductsFetch(updatedState);
  return updatedState;
};


const isOrderDetailsFetch = (resource, subResource) => resource === 'orders' && subResource === 'status';

/**
 * This enables refreshing the /orders/:order_id/items page
 * There is no /orders/:order_id API route yet, but there is a /orders/:order_id/status route that gives back similar information.
 */
const handleOrderDetailsFetch = (state, action) => {
  if (action.error) return state;
  if (action.response) {
    let sameIdOrderExists = false;
    // Depends on running GET /orders/?start=whatever&count=whatever first, so that state.get('orders') has an array value
    state.get('orders').map((order) => {
      if (order.id !== action.response.id) return order;
      sameIdOrderExists = true;
      return action.response;
    });
    if (!sameIdOrderExists) state.get('orders').push(action.response);
  }
  return state;
};

const handleProductsFetch = (state) => {
  let updatedState = setProductsOnFilteredProducts(state);
  return warnWhenNoItems(updatedState);
};

/**
 * The product table in the menu page uses the state 'filteredProducts'
 * If the 'products' state updated, the 'filteredProducts' also need to be updated
 */
const setProductsOnFilteredProducts = (updatedState) => {
  updatedState = updatedState.set('filteredProducts', updatedState.get('products'));
  return updatedState;
};

const warnWhenNoItems = (updatedState) => {
  updatedState.get('products').map((product) => {
    product.hasWarning = product.items && product.items.length === 0;
  });
  updatedState = updatedState.set('products', updatedState.get('products'));
  return updatedState;
};

const getPathParts = (path, action) => {
  let [resource, resourceId, subResource] = path;
  let queryParam = null;

  // For duplicating catering zones, to trigger the `update` function to update the location with the new duplicated catering zone,
  // remove the last word from the resourcePath array so that `path` is ['catering-zones', <cateringZoneId>], skipping the (path.length > 2)
  // block, and moving straight to `let updatedState = update(state, action, resource, resourceId, queryParam)`
  if ((path.length === 5 && subResource === 'catering-zones' && path[4] === 'duplicate') || (path.length === 3 && subResource === 'duplicate')) {
    path.pop();
  }

  // Check if there is a query parameter
  if (path.length === 1 && typeof resource === 'string' && resource.includes('?')) {
    const queryParams = resource.split('?');
    resource = queryParams[0];
    queryParam = queryParams[1];
  }

  if (resourceId === 'square-server') {
    resourceId = action.response.id;
  }
  return {
    resourceId,
    resource,
    subResource,
    queryParam,
  };
};

const updateChildResourceListOnParent = ({
  parentState,
  updatedSingleChildResource,
  resourceId,
  resource,
  subResource,
  action,
  path,
}) => {
  const updateMatchingIdChildResources = (childResource) => {
    if (childResource.id != resourceId) return childResource;

    if (path.length === 4 && resource === 'locations' && subResource === 'square-server') return action.response;
    if (subResource === 'items') childResource.hasWarning = false;

    return Map(childResource).merge(updatedSingleChildResource).toJS();
  };

  const setUpdatedChildResourceInList = resourceList => resourceList.map(updateMatchingIdChildResources);

  return parentState.update(resource, setUpdatedChildResourceInList);
};

const getStateWithPagination = (resource, action, state) => {
  const paginationInfo = {
    [resource]: {},
  };
  let list = [];
  Object.keys(action.response).forEach((key) => {
    if (key === 'start' || key === 'count' || key === 'totalSize') {
      // Add the start, count, and totalSize to the pagination object
      paginationInfo[resource][key] = action.response[key];
    } else {
      // Initialize list as the list of paginated responses
      list = action.response[key];
    }
  });
  if (!state.has('pagination')) {
    // if the first time request paginated resource
    const resourceList = action.response !== undefined ? list : [];
    return state.set(resource, resourceList).merge({ pagination: paginationInfo });
  } else if (paginationInfo[resource].start === 0) {
    // if request another paginated resource or request fetched paginated resource again
    // e.g each time enter a resource page will trigger a fetch to make sure the resource is up to date
    const newPaginationState = state.get('pagination').merge(paginationInfo);
    return state.set(resource, list).merge({ pagination: newPaginationState });
  } else {
    // if update paginated resource. e.g request next 10 items
    const newResourceList = state.has(resource) ? state.get(resource).concat(list) : list;
    const newPaginationState = state.get('pagination').merge(paginationInfo);
    return state.set(resource, newResourceList).merge({ pagination: newPaginationState });
  }
};

const update = (state, action, resource, resourceId, queryParam) => {
  const { resourcePath } = action;
  if (resource === 'time-limits') resource = 'timeLimits';
  switch (action.type) {
    case resourcesActionTypes.DELETE_RESOURCE_SUCCESS:
      const mainDeleter = stateMap => stateMap.update(resource, (resources) => {
        if (resources !== undefined) {
          return resources.filter(res => res.id != resourceId);
        }
        return resources;
      });

      if (resourcePath[0] === 'categories' && resourcePath.length === 2) {
        const id = resourcePath[1];
        const stateWithCategoryDeleted = mainDeleter(state);
        const categoryIdsIsCheckedMap = state.getIn(['pageSettings', 'menu', 'categoryIdsIsCheckedMap'], null);
        if (!categoryIdsIsCheckedMap) return stateWithCategoryDeleted;
        const isCheckedMapCopy = Map(categoryIdsIsCheckedMap);
        const categoryDeletedFromIsChecked = isCheckedMapCopy.filter((val, key) => key !== `${id}`);
        return stateWithCategoryDeleted.setIn(['pageSettings', 'menu', 'categoryIdsIsCheckedMap'], categoryDeletedFromIsChecked.toJS());
      } else {
        return mainDeleter(state);
      }
    case resourcesActionTypes.ADD_RESOURCE_SUCCESS:
      const mainAdder = stateMap => stateMap.update(resource, (resources) => {
        if (resources !== undefined) {
          return resources.concat(action.response);
        }
        return resources;
      });

      if (resource === 'orders/search' && queryParam) {
        return getStateWithPagination('orders', action, state);
      }
      if (resourcePath[0] === 'categories' && resourcePath.length === 1) {
        const { id } = action.response;
        const stateWithNewCategory = mainAdder(state);
        const categoryIdsIsCheckedMap = state.getIn(['pageSettings', 'menu', 'categoryIdsIsCheckedMap'], null);
        if (!categoryIdsIsCheckedMap) return stateWithNewCategory;
        const isCheckedMapCopy = Map(categoryIdsIsCheckedMap);
        const categoryAddedToIsChecked = isCheckedMapCopy.set(`${id}`, true);
        return stateWithNewCategory.setIn(['pageSettings', 'menu', 'categoryIdsIsCheckedMap'], categoryAddedToIsChecked.toJS());
      } else {
        return mainAdder(state);
      }
    case resourcesActionTypes.UPDATE_RESOURCE_SUCCESS:
      if (isProductOutOfStock(resource, resourcePath)) {
        const outOfStockLocationsForMenu = action.response.map(location => location.id);
        // state in this iteration will be the product object
        const stateWithOutOfStockForDisplayInMenu = state.set('outOfStockLocations', outOfStockLocationsForMenu);
        return updateResourceSuccess(stateWithOutOfStockForDisplayInMenu, action, resource, resourceId, queryParam);
      } else if (isOptionsOutOfStock(resource, resourcePath)) {
        return state.set('outOfStockLocations', action.response);
      } else {
        return updateResourceSuccess(state, action, resource, resourceId, queryParam);
      }
    case resourcesActionTypes.GET_ALL_RESOURCES_SUCCESS:
    case resourcesActionTypes.BATCH_UPDATE_AFTER_GET_ALL_RESOURCES:
      if (resource === 'options' && queryParam) {
        const searchParamsObj = new URLSearchParams(queryParam);
        const filterParam = searchParamsObj.get('filter');
        if (!filterParam) return getStateWithPagination(resource, action, state);

        const [filterResourceName, filterResourceId] = filterParam.split(':');
        const updatedOptionsOnCustomization = state.update('customizations', (customizations) => {
          return customizations.map((customization) => {
            if (Number(filterResourceId) !== customization.id) return customization;
            const customizationClone = cloneDeep(customization);
            customizationClone.options = action.response.optionList;
            return customizationClone;
          });
        });

        const nextState = getStateWithPagination(resource, action, updatedOptionsOnCustomization);
        return nextState;
      }
      if (resource === 'orders/v2' && queryParam) {
        return getStateWithPagination('orders', action, state);
      }
      if (queryParam) {
        return getStateWithPagination(resource, action, state);
      }

      if (resource === 'companies') {
        return state.set(resource, action.response !== undefined
          ? [action.response]
          : []);
      }

      return state.set(resource, action.response !== undefined
        ? action.response
        : []);
    case resourcesActionTypes.REPRINT_ORDER_FAILURE:
    case resourcesActionTypes.REPRINT_ORDER_SUCCESS:
      return state;
    default:
      return state;
  }
};

const updateResourceSuccess = (stateMap, action, resourceName, resourceId, queryParam) => {
  const { resourcePath, response } = action;
  const idToResultMap = {};
  if (isBulkAction(resourcePath)) {
    if (Array.isArray(response)) response.forEach(resource => idToResultMap[resource.id] = resource);
  }

  return stateMap.update(resourceName, (resources) => {
    if (resourceName === 'locations' && resources !== undefined) {
      return resources.filter(res => res.id != resourceId);
    } else if (resources !== undefined && resourceId) {
      return resources.map((res) => {
        if (isBulkAction(resourcePath)) {
          if (resourcePath[0] === 'products' && idToResultMap[res.id]) {
            const newProductMap = Map(idToResultMap[res.id]);
            // The response from /products/disable does not include the outOfStockLocations key used in DisplayResources to show that status
            return keepPrevProductOutOfStockLocations(res, newProductMap);
          }
          if (resourcePath[0] === 'products' && !idToResultMap[res.id]) return res;
          // Handles future cases of bulk actions
          if (idToResultMap[res.id]) return idToResultMap[res.id];
        }
        if (res.id.toString() === resourceId.toString()) {
          if (Array.isArray(resourcePath) && resourcePath.length === 2 && resourcePath[0] === 'products') {
            // If PUT ['products', <id>] dispatches updateResourceSuccess after PUT ['products', <id>, 'out-of-stock'], the PUT /products/:id
            // request can overwrite the product's `outOfStockLocations` value, used for display in the Full Menu page for the Product List
            const newProductMap = Map(action.response);
            return keepPrevProductOutOfStockLocations(res, newProductMap);
          }
          if (resourcePath.length === 2 && resourcePath[0] === 'orders') {
            let clonedResource = cloneDeep(res);
            clonedResource.orderStatus = action.response.orderStatus;
            return clonedResource;
          }
          return action.response;
        }
        return res;
      });
    } else if (resourceName === 'companies') {
      resources[0] = action.response;
    // This block is what updates Product and Item out of stock, since both satisfy resourceName === 'out-of-stock'
    // as both have 'out-of-stock' as the last element in an odd-number length action.resourcePath array.
    // TODO: Harmonize both product and item out of stock to use 'outOfStock' key in Redux store. Currently in
    // CreateAndEditResource's component state for form submission, products initialize out of stock locations under the
    // 'outOfStock' key, while items use an 'out-of-stock' key.
    } else if (resourceName === 'out-of-stock') {
      resources = action.response;
    }
    return resources;
  });
};

const keepPrevProductOutOfStockLocations = (prevProduct, newProductMap) => {
  const prevProductOutOfStockLocationsForMenuDisplay = prevProduct.outOfStockLocations;
  const finalProduct = newProductMap.set('outOfStockLocations', prevProductOutOfStockLocationsForMenuDisplay).toJS();
  return finalProduct;
};

const isBulkAction = resourcePath => Array.isArray(resourcePath) && resourcePath.length === 2 && Object.values(bulkActions).map(action => action.uri).includes(resourcePath[1]);

const getFirstResourcePathSegment = action => action && Array.isArray(action.resourcePath) && action.resourcePath[0];
const isGetAllResourcesSuccess = action => action.type === resourcesActionTypes.GET_ALL_RESOURCES_SUCCESS;

const isGetOrdersAnalyticsSuccess = (action) => {
  const firstResourcePathSegment = getFirstResourcePathSegment(action);
  return isGetAllResourcesSuccess(action) && ChartUtils.isHomeAnalyticsRoute(firstResourcePathSegment);
};

const isGetAppDownloadsSuccess = (action) => {
  const firstResourcePathSegment = getFirstResourcePathSegment(action);
  return isGetAllResourcesSuccess(action) && ChartUtils.isGetAppDownloadsRoute(firstResourcePathSegment);
};

const isProductOutOfStock = (recursiveCurrentPathSegment, fullResourcePath) => {
  const isMergingOOS = isMergingOutOfStock(recursiveCurrentPathSegment);
  const isProductOutOfStockAction = Array.isArray(fullResourcePath) && fullResourcePath.length === 3
    && fullResourcePath[0] === 'products' && fullResourcePath[2] === 'out-of-stock';
  return isMergingOOS && isProductOutOfStockAction;
};

const isOptionsOutOfStock = (recursiveCurrentPathSegment, fullResourcePath) => {
  const isMergingOOS = recursiveCurrentPathSegment === 'out-of-stock';
  const isOptionsOutOfStockAction = Array.isArray(fullResourcePath) && fullResourcePath.length === 3
    && fullResourcePath[0] === 'options' && fullResourcePath[2] === 'out-of-stock';
  return isMergingOOS && isOptionsOutOfStockAction;
};

const isMergingOutOfStock = recursiveCurrentPathSegment => recursiveCurrentPathSegment === 'out-of-stock';
